import { hexToRgb } from '@/common/theme/colorMath';

import {
  nebulaHashNoise,
  type NebulaFormation,
} from '@/common/components/presence/nebula/nebulaVisualModel';

export type ContourPoint = { x: number; y: number };

export type FormationLobeSpec = {
  ox: number;
  oy: number;
  lrx: number;
  lry: number;
  alpha: number;
  rotation: number;
};

/** Deterministic contour wobble shared by canvas and static SVG renderers. */
export function nebulaContourNoise(angle: number, phase: number, seed: number): number {
  const primary = Math.sin(angle * 3 + phase * 1.15 + seed * 0.37);
  const secondary = Math.sin(angle * 5 - phase * 0.72 + seed * 0.19);
  return 0.58 + primary * 0.2 + secondary * 0.1;
}

export function sampleFormationContourPoints(
  formation: NebulaFormation,
  cx: number,
  cy: number,
  silhouetteRadius: number,
  segments = 14,
): ContourPoint[] {
  const px = cx + formation.centerX * silhouetteRadius;
  const py = cy + formation.centerY * silhouetteRadius;
  const baseRx = formation.scaleX * silhouetteRadius * 0.58;
  const baseRy = formation.scaleY * silhouetteRadius * 0.58;
  const cos = Math.cos(formation.rotation);
  const sin = Math.sin(formation.rotation);

  const points: ContourPoint[] = [];
  for (let i = 0; i < segments; i += 1) {
    const angle = (i / segments) * Math.PI * 2;
    const roughness = 1 + (nebulaContourNoise(angle, formation.phase, formation.seed) - 0.58)
      * formation.contourRoughness * 2.4;
    const lx = Math.cos(angle) * baseRx * roughness;
    const ly = Math.sin(angle) * baseRy * roughness;
    points.push({
      x: px + lx * cos - ly * sin,
      y: py + lx * sin + ly * cos,
    });
  }
  return points;
}

export function contourPointsToPath(points: ContourPoint[]): string {
  if (points.length === 0) return '';
  const [first, ...rest] = points;
  let d = `M ${first.x.toFixed(3)} ${first.y.toFixed(3)}`;
  rest.forEach((point) => {
    d += ` L ${point.x.toFixed(3)} ${point.y.toFixed(3)}`;
  });
  return `${d} Z`;
}

export function buildFormationContourPath(
  formation: NebulaFormation,
  cx: number,
  cy: number,
  silhouetteRadius: number,
  segments = 14,
): string {
  return contourPointsToPath(
    sampleFormationContourPoints(formation, cx, cy, silhouetteRadius, segments),
  );
}

/**
 * Density lobes inside a parcel.
 *
 * They ride out near the contour and past it, at strongly varying radii, on
 * each axis separately. Tight, similar lobes huddled around the centre just
 * rebuild the puff the contour was supposed to hide.
 */
export function buildFormationLobeSpecs(
  formation: NebulaFormation,
  silhouetteRadius: number,
  size: number,
): FormationLobeSpec[] {
  const spanX = formation.scaleX * silhouetteRadius * 0.58;
  const spanY = formation.scaleY * silhouetteRadius * 0.58;
  const baseR = Math.min(spanX, spanY);
  const lobeCount = size <= 28 ? 4 : 8;
  const morph = formation.morph ?? 0;
  const swirl = formation.swirl ?? 1;
  const lobes: FormationLobeSpec[] = [];

  for (let i = 0; i < lobeCount; i += 1) {
    const orbit = (i / lobeCount) * Math.PI * 2;
    const wobble = (nebulaContourNoise(orbit, formation.phase, formation.seed) - 0.58) * 0.7;
    const angle = orbit + wobble + formation.phase * 0.18 * swirl + morph * 0.12;
    const puff = nebulaContourNoise(angle, formation.phase * 0.9, formation.seed + i * 9);
    const spread = nebulaHashNoise(i, formation.seed, 31);
    const travel = 0.4 + puff * 0.52;
    const radius = baseR * (0.28 + spread * 0.46);
    lobes.push({
      ox: Math.cos(angle) * spanX * travel,
      oy: Math.sin(angle) * spanY * travel,
      lrx: radius,
      lry: radius * (0.74 + nebulaHashNoise(i, formation.seed, 17) * 0.42),
      alpha: formation.alpha * (0.3 + puff * 0.42),
      rotation: 0,
    });
  }

  return lobes;
}

export function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

type Rgb = [number, number, number];

export function parseNebulaRgb(hex: string): Rgb {
  const rgb = hexToRgb(hex);
  return rgb ? [rgb.r, rgb.g, rgb.b] : [0, 0, 0];
}

export function liftNebulaRgb(rgb: Rgb, amount: number): Rgb {
  return rgb.map((channel) => Math.round(channel + (255 - channel) * amount)) as Rgb;
}

/** Blend toward an arbitrary colour — used to grade the well toward tenant depth. */
export function mixNebulaRgb(rgb: Rgb, target: Rgb, amount: number): Rgb {
  return rgb.map(
    (channel, index) => Math.round(channel + (target[index] - channel) * amount),
  ) as Rgb;
}
